import './AppStatusTable.scss';

import { AppStatusMessage } from './AppStatusMessage';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { getColumns } from './AppStatusTableColumns';
import { queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { SonarCard } from '@ui/general';

import { coreActions, DataSecurityAppActiveStatus, FeatureFlag } from '@sonar-software/sonar-core';
import { PaginatedTable, PaginatedTableQueryParams } from '@ui/tables/PaginatedTable';
import React, { useMemo, useState } from 'react';
import { useAction, useAsyncState, useFeatureFlag, useOnMount, usePrevious, useQueryParams } from '@hooks';

const getRowKey = (result: DataSecurityAppActiveStatus) => `${result.applicationName}-${result.applicationLabel}`;

const dataSecurityAppStatusSelectors = createAsyncSelectors((state) => state.core.dataSecurity.appStaus.get);

export type AppStatusTableQueryParams = PaginatedTableQueryParams & {
	applicationId?: number;
};

const convertToDql = (params: AppStatusTableQueryParams) => queryParamsToDynamicQueryLanguageParams(params, {
	filterParams: { applicationId: true }
});

export interface AppStatusTableProps {
	applicationId?: number;
}

export const AppStatusTable: React.FC<AppStatusTableProps> = ({ applicationId }) => {
	const getAppStatus = useAction(coreActions.requestGetCoreDataSecurityAppStatus);
	const [ firstLoad, setFirstLoad ] = useState(true);
	const previousApplicationId = usePrevious(applicationId);
	const applicationIdChanged = applicationId !== previousApplicationId;
	const isEnabled = useFeatureFlag(FeatureFlag.DataSecurityApplicationStatus);

	const { data, isRequested } = useAsyncState(dataSecurityAppStatusSelectors);

	const statusLink = Number.isInteger(applicationId) ? data?.results[ 0 ]?.statusPageUrl : undefined;
	const statusMessage = Number.isInteger(applicationId) ? data?.results[ 0 ]?.errorMessage : undefined;

	const { queryParams, updateParams } = useQueryParams<AppStatusTableQueryParams>({
		defaultParams: {
			applicationId,
			count: 10
		},
		onUpdate: () => {
			getAppStatus(
				convertToDql(queryParams)
			);
		}
	});

	useOnMount(() => {
		setFirstLoad(false);
		getAppStatus(
			convertToDql(queryParams)
		);
	});

	const columns = useMemo(() => getColumns(!Number.isInteger(applicationId)), [ applicationId ]);

	if (!isEnabled) {
		return null;
	}

	return (
		<SonarCard
			className='app-status-table'
			loading={ firstLoad || applicationIdChanged }
			noSpacing
			title={ COPY_SPECIFIC.APPLICATION_STATUS }
			titleClassName='h2'
			useSpinnerLoading>
			<div className='table-container'>
				<PaginatedTable
					autohidePagination
					columns={ columns }
					defaultPageSize={ 10 }
					id='app-status-table'
					loading={ isRequested }
					onChange={ updateParams }
					resource={ data }
					rowKey={ getRowKey }
					showSizeChanger={ false }
					sortBy={ queryParams.sortBy }
					sortDirection={ queryParams.sortDirection } />
			</div>
			<AppStatusMessage
				link={ statusLink }
				message={ statusMessage } />
		</SonarCard>
	);
};
